/**
 * EmailPostureCard.jsx — Phase 12.3 · Trust Center freshness card.
 *
 * Pulls `/api/public/trust/email-posture` and renders a compact
 * green/yellow/red card per sending domain. Cacheable, no auth.
 * Public payload deliberately omits raw SPF / DKIM / DMARC values —
 * we only surface counts + statuses (cleanest signal, no help for
 * spear-phishers reverse-engineering our posture).
 *
 * Render contract:
 *   - 1+ rows per domain (qortrace.com, qorbom.com)
 *   - status pill: GREEN ok / AMBER warn / RED fail
 *   - issue count + "checked Xm ago"
 *   - A small "Why this matters" footnote linking to the
 *     methodology page.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ShieldX, Mail, Clock } from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;


const STATUS_META = {
  ok:   { color: "#1FDD83", label: "OK",   icon: ShieldCheck },
  warn: { color: "#FFB000", label: "WARN", icon: ShieldAlert },
  fail: { color: "#FF6E6E", label: "FAIL", icon: ShieldX },
};


export default function EmailPostureCard() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/trust/email-posture`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load posture.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <CardShell title="Email auth posture">
        <p className="text-xs" style={{ color: "var(--qt-text-3)" }}>
          Loading…
        </p>
      </CardShell>
    );
  }
  if (err || !data?.items?.length) {
    return (
      <CardShell title="Email auth posture">
        <p className="text-xs" style={{ color: "var(--qt-text-3)" }}>
          Self-check pending. Re-run daily at 06:15 UTC.
        </p>
      </CardShell>
    );
  }

  const worst = data.worst_status || "ok";
  const meta  = STATUS_META[worst] || STATUS_META.ok;

  return (
    <CardShell title="Email auth posture" pillColor={meta.color} pillText={meta.label}>
      <div data-testid="trust-email-posture-grid"
           style={{
             display: "grid",
             gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
             gap: 14, marginTop: 12,
           }}>
        {data.items.map((it) => (
          <DomainRow key={it.domain} item={it} />
        ))}
      </div>
      <p className="text-xs mt-4" style={{ color: "var(--qt-text-3)",
                                            lineHeight: 1.55 }}>
        We monitor our own SPF, DKIM, and DMARC daily so any DNS drift
        is caught within 24&nbsp;hours. <a href="/methodology#email"
           style={{ color: "var(--qt-cyan)", textDecoration: "underline" }}>
          Why this matters →</a>
      </p>
    </CardShell>
  );
}


function DomainRow({ item }) {
  const meta = STATUS_META[item.overall] || STATUS_META.ok;
  const Icon = meta.icon;
  return (
    <div data-testid={`trust-email-posture-row-${item.domain}`}
         style={{
           background: "rgba(255,255,255,0.02)",
           border: `1px solid ${meta.color}33`,
           borderLeft: `3px solid ${meta.color}`,
           borderRadius: 4,
           padding: "12px 14px",
         }}>
      <div className="flex items-center gap-2 mb-1">
        <Mail size={11} style={{ color: "var(--qt-text-3)" }} />
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-text-1)",
                       letterSpacing: "0.04em" }}>
          {item.domain.toUpperCase()}
        </span>
        <span className="qt-mono text-[10px] ml-auto flex items-center gap-1"
              style={{ color: meta.color, letterSpacing: "0.08em" }}>
          <Icon size={11} />
          {meta.label}
        </span>
      </div>
      <div className="text-xs flex items-center gap-3"
           style={{ color: "var(--qt-text-3)" }}>
        {item.issue_count === 0 ? (
          <span>Clean — SPF · DKIM · DMARC verified.</span>
        ) : (
          <span style={{ color: meta.color }}>
            {item.issue_count} issue{item.issue_count === 1 ? "" : "s"}
          </span>
        )}
        <span style={{ marginLeft: "auto",
                       display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Clock size={10} />
          {fmtRelative(item.checked_at)}
        </span>
      </div>
    </div>
  );
}


function CardShell({ title, pillColor, pillText, children }) {
  return (
    <section className="mt-16" data-testid="trust-email-posture-section">
      <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>
        EMAIL AUTHENTICATION
      </div>
      <div className="flex items-center gap-3 mt-2">
        <h2 className="text-2xl font-semibold"
            style={{ color: "#fff" }}>{title}</h2>
        {pillColor && (
          <span className="qt-mono text-[10px]"
                style={{
                  padding: "2px 7px", borderRadius: 2,
                  background: `${pillColor}22`,
                  color: pillColor, letterSpacing: "0.08em",
                }}>
            {pillText}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}


function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
  return `${Math.floor(diffMin / (60 * 24))}d ago`;
}
